// The single plain-language renderer for schedule windows (docs/06 UX principle
// #5). Reused by the Schedules table, the schedule form, and later the
// assistant's confirmation cards — keep it pure and framework-free.

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const EVERY_DAY = 0b1111111; // 127
const WEEKDAYS = 0b0011111; // Mon–Fri (bits 0–4) = 31
const WEEKENDS = 0b1100000; // Sat, Sun (bits 5,6) = 96

export interface ScheduleWindowInput {
  days_mask: number;
  start_time: string | null;
  end_time: string | null;
  start_date: string | null;
  end_date: string | null;
}

export interface SummaryOptions {
  // Year to compare against when deciding whether to print the year on dates.
  // Defaults to the current year; pass explicitly in tests for determinism.
  referenceYear?: number;
}

/** Set bits of the day mask, in Mon..Sun order. */
function activeDays(mask: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < 7; i++) if (mask & (1 << i)) out.push(i);
  return out;
}

/** "Mon–Fri", "Sat–Sun", "Every day", "Mon, Wed, Fri", "Mon–Wed, Sat". */
export function daysSummary(mask: number): string {
  const m = mask & EVERY_DAY;
  if (m === EVERY_DAY) return 'Every day';
  if (m === WEEKDAYS) return 'Mon–Fri';
  if (m === WEEKENDS) return 'Sat–Sun';
  const days = activeDays(m);
  if (days.length === 0) return 'Never';

  // Collapse consecutive runs into ranges: [0,1,2,5] → "Mon–Wed, Sat".
  const runs: Array<[number, number]> = [];
  for (const d of days) {
    const last = runs[runs.length - 1];
    if (last && d === last[1] + 1) last[1] = d;
    else runs.push([d, d]);
  }
  return runs
    .map(([a, b]) =>
      a === b
        ? DAY_LABELS[a]
        : b === a + 1
          ? `${DAY_LABELS[a]}, ${DAY_LABELS[b]}` // two days: comma reads better than a dash
          : `${DAY_LABELS[a]}–${DAY_LABELS[b]}`,
    )
    .join(', ');
}

/** "18:00–22:00", "" when all-day, "22:00–02:00 (overnight)" when it wraps. */
export function timeSummary(start: string | null, end: string | null): string {
  if (!start && !end) return '';
  if (start && !end) return `from ${start}`;
  if (!start && end) return `until ${end}`;
  const overnight = (start as string) >= (end as string);
  return `${start}–${end}${overnight ? ' (overnight)' : ''}`;
}

function formatDate(iso: string, referenceYear: number): string {
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  const month = MONTHS[(m ?? 1) - 1] ?? '';
  const base = `${month} ${d}`;
  return y === referenceYear ? base : `${base}, ${y}`;
}

/** "", "from Jul 15", "until Jul 31", "Jul 15 – Jul 31". */
export function dateRangeSummary(
  start: string | null,
  end: string | null,
  opts: SummaryOptions = {},
): string {
  const refYear = opts.referenceYear ?? new Date().getFullYear();
  if (!start && !end) return '';
  if (start && !end) return `from ${formatDate(start, refYear)}`;
  if (!start && end) return `until ${formatDate(end, refYear)}`;
  return `${formatDate(start as string, refYear)} – ${formatDate(end as string, refYear)}`;
}

/**
 * Full window in plain words, e.g. "Mon–Fri 18:00–22:00 until Jul 31".
 * A truly unbounded, all-day, every-day schedule reads as "Always on".
 */
export function scheduleWindowSummary(
  s: ScheduleWindowInput,
  opts: SummaryOptions = {},
): string {
  const days = daysSummary(s.days_mask);
  const time = timeSummary(s.start_time, s.end_time);
  const dates = dateRangeSummary(s.start_date, s.end_date, opts);

  if (days === 'Every day' && !time && !dates) return 'Always on';

  return [days, time, dates].filter(Boolean).join(' ');
}

/**
 * Conflict phrasing for the schedule form / assistant cards, e.g.
 * "Overlaps with 'Base loop' — this one wins (higher priority)".
 */
export function conflictSummary(
  otherPlaylistName: string,
  thisWins: boolean,
  reason: 'priority' | 'specificity' | 'newer',
): string {
  const why =
    reason === 'priority'
      ? 'higher priority'
      : reason === 'specificity'
        ? 'more specific target'
        : 'created more recently';
  return thisWins
    ? `Overlaps with “${otherPlaylistName}” — this one wins (${why}).`
    : `Overlaps with “${otherPlaylistName}” — that one wins (${why}).`;
}
